import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Button, Checkbox, Typogaphy } from 'vnetwork-ui'
import {
    Download,
    ImageDown,
    Users,
    Briefcase,
    UserX,
    Lightbulb,
    Trophy,
    AlertCircle,
} from 'lucide-react'
import html2canvas from 'html2canvas'
import * as XLSX from 'xlsx'
import { EmployeeReportItem } from '../types'
import { SCHEMES } from '@/components/EchartCustomized'

interface EmployeeReportProps {
    data: EmployeeReportItem[]
    employeeType: 'account_manager' | 'sale_manager'
    fromDate: string
    toDate: string
}

const COLORS = {
    excellent: SCHEMES.success[0], // retention >= 95%
    good: SCHEMES.info[0], // retention 90-95%
    average: SCHEMES.warning[0], // retention 80-90%
    poor: SCHEMES.error[0], // retention < 80%
}

const getRetentionColor = (rate: number) => {
    if (rate >= 95) return COLORS.excellent
    if (rate >= 90) return COLORS.good
    if (rate >= 80) return COLORS.average
    return COLORS.poor
}

const EmployeeReport: React.FC<EmployeeReportProps> = ({
    data,
    employeeType,
    fromDate,
    toDate,
}) => {
    const { t } = useTranslation()
    const chartRef = useRef<HTMLDivElement>(null)
    const [showInactive, setShowInactive] = useState(false)

    // Filter data based on showInactive toggle
    const filteredData = useMemo(() => {
        if (showInactive) return data
        return data.filter((item) => item.employee_status === 1)
    }, [data, showInactive])

    // Count inactive employees
    const inactiveCount = useMemo(() => {
        return data.filter((item) => item.employee_status === 0).length
    }, [data])

    const chartData = useMemo(() => {
        return filteredData
            .sort((a, b) => b.total_services - a.total_services)
            .slice(0, 15) // Top 15 employees
            .map((item) => ({
                ...item,
                color: getRetentionColor(item.retention_rate),
                displayName: item.employee_name
                    ? item.employee_name.length > 10
                        ? item.employee_name.substring(0, 10) + '...'
                        : item.employee_name
                    : String(item.employee_code),
            }))
    }, [filteredData])

    const totalEmployees = filteredData.length
    const totalServices = useMemo(() => {
        return filteredData.reduce((sum, d) => sum + d.total_services, 0)
    }, [filteredData])

    const avgRetentionRate = useMemo(() => {
        // Only include employees with services/customers in average calculation
        const employeesWithData = filteredData.filter(
            (d) => d.total_services > 0 || d.total_customers > 0
        )
        if (employeesWithData.length === 0) return 0
        return (
            employeesWithData.reduce((sum, d) => sum + d.retention_rate, 0) /
            employeesWithData.length
        )
    }, [filteredData])

    // Analysis data
    const analysis = useMemo(() => {
        const employeesWithData = filteredData.filter(
            (d) => d.total_services > 0 || d.total_customers > 0
        )
        if (employeesWithData.length < 2) return null

        // Top performer - highest retention rate with at least some services
        const sortedByRetention = [...employeesWithData].sort(
            (a, b) => b.retention_rate - a.retention_rate
        )
        const topPerformer = sortedByRetention[0]
        const needsAttention = sortedByRetention[sortedByRetention.length - 1]

        // Most services managed
        const sortedByServices = [...employeesWithData].sort(
            (a, b) => b.total_services - a.total_services
        )
        const mostServices = sortedByServices[0]

        // Performance distribution
        const excellent = employeesWithData.filter(
            (d) => d.retention_rate >= 95
        ).length
        const good = employeesWithData.filter(
            (d) => d.retention_rate >= 90 && d.retention_rate < 95
        ).length
        const average = employeesWithData.filter(
            (d) => d.retention_rate >= 80 && d.retention_rate < 90
        ).length
        const poor = employeesWithData.filter(
            (d) => d.retention_rate < 80
        ).length

        // Total new and cancelled
        const totalNew = filteredData.reduce(
            (sum, d) => sum + d.new_services,
            0
        )
        const totalCancelled = filteredData.reduce(
            (sum, d) => sum + d.cancelled_services,
            0
        )

        return {
            topPerformer,
            needsAttention,
            mostServices,
            distribution: { excellent, good, average, poor },
            totalNew,
            totalCancelled,
        }
    }, [filteredData])

    const exportToImage = async () => {
        if (!chartRef.current) return
        const canvas = await html2canvas(chartRef.current, {
            backgroundColor: '#ffffff',
            scale: 2,
        })
        const link = document.createElement('a')
        link.download = `employee_report_${employeeType}_${fromDate}_${toDate}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
    }

    const exportToExcel = () => {
        const exportData = filteredData.map((item) => ({
            [t('employee_code')]: item.employee_code,
            [t('employee_name')]: item.employee_name || '-',
            [t('status')]:
                item.employee_status === 1 ? t('active') : t('inactive'),
            [t('employee_type')]:
                item.employee_type === 'account_manager'
                    ? 'Account Manager'
                    : 'Sale Manager',
            [t('total_services')]: item.total_services,
            [t('new_services')]: item.new_services,
            [t('cancelled_services')]: item.cancelled_services,
            [t('lost_revenue')]: item.lost_revenue,
            [t('retention_rate')]: `${item.retention_rate.toFixed(1)}%`,
            [t('total_customers')]: item.total_customers,
        }))

        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet(exportData)
        XLSX.utils.book_append_sheet(wb, ws, t('employee_report'))
        XLSX.writeFile(
            wb,
            `employee_report_${employeeType}_${fromDate}_${toDate}.xlsx`
        )
    }

    const formatCurrency = (amount: number) => {
        if (amount >= 1000000000) {
            return `${(amount / 1000000000).toFixed(1)}B`
        }
        if (amount >= 1000000) {
            return `${(amount / 1000000).toFixed(1)}M`
        }
        return amount.toLocaleString()
    }

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || !payload.length) return null

        const item = payload[0].payload as EmployeeReportItem & {
            color: string
        }

        return (
            <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                <div className="flex items-center gap-2 mb-2">
                    <p className="font-semibold text-gray-800">
                        {item.employee_name || item.employee_code}
                    </p>
                    {item.employee_status === 0 && (
                        <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-600 rounded">
                            {t('inactive')}
                        </span>
                    )}
                </div>
                <div className="space-y-1 text-sm">
                    <p>
                        {t('total_services')}:{' '}
                        <span className="font-medium">
                            {item.total_services.toLocaleString()}
                        </span>
                    </p>
                    <p>
                        {t('total_customers')}:{' '}
                        <span className="font-medium">
                            {item.total_customers.toLocaleString()}
                        </span>
                    </p>
                    <p
                        className={
                            item.new_services
                                ? 'text-green-600'
                                : 'text-gray-400'
                        }
                    >
                        {t('new_services')}:{' '}
                        <span className="font-medium">
                            {item.new_services
                                ? `+${item.new_services.toLocaleString()}`
                                : '-'}
                        </span>
                    </p>
                    <p
                        className={
                            item.cancelled_services
                                ? 'text-red-600'
                                : 'text-gray-400'
                        }
                    >
                        {t('cancelled_services')}:{' '}
                        <span className="font-medium">
                            {item.cancelled_services
                                ? `-${item.cancelled_services.toLocaleString()}`
                                : '-'}
                        </span>
                    </p>
                    <p className="text-purple-600">
                        {t('lost_revenue')}:{' '}
                        <span className="font-medium">
                            {formatCurrency(item.lost_revenue)}
                        </span>
                    </p>
                    <p
                        className="pt-1 border-t border-gray-100 font-medium"
                        style={{ color: item.color }}
                    >
                        {t('retention_rate')}: {item.retention_rate.toFixed(1)}%
                    </p>
                </div>
            </div>
        )
    }

    const title =
        employeeType === 'account_manager'
            ? t('account_manager_report')
            : t('sale_manager_report')

    return (
        <div className="space-y-4">
            {/* Chart Section */}
            <div
                ref={chartRef}
                className="bg-white rounded-md p-4 shadow-sm border border-gray-100"
            >
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <div
                            className={`p-2 rounded-lg ${employeeType === 'account_manager' ? 'bg-blue-100' : 'bg-purple-100'}`}
                        >
                            {employeeType === 'account_manager' ? (
                                <Users size={20} className="text-blue-600" />
                            ) : (
                                <Briefcase
                                    size={20}
                                    className="text-purple-600"
                                />
                            )}
                        </div>
                        <div>
                            <Typogaphy className="text-lg font-semibold text-gray-800">
                                {title}
                            </Typogaphy>
                            <Typogaphy className="text-sm text-gray-500">
                                {t('service_statistics_by_employee')}
                            </Typogaphy>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex gap-4 text-right">
                            <div>
                                <Typogaphy className="text-xl font-bold text-gray-700">
                                    {totalEmployees}
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-500">
                                    {t('employees')}
                                </Typogaphy>
                            </div>
                            <div>
                                <Typogaphy className="text-xl font-bold text-blue-600">
                                    {totalServices.toLocaleString()}
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-500">
                                    {t('total_services')}
                                </Typogaphy>
                            </div>
                            <div>
                                <Typogaphy className="text-xl font-bold text-green-600">
                                    {avgRetentionRate.toFixed(1)}%
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-500">
                                    {t('avg_retention_rate')}
                                </Typogaphy>
                            </div>
                        </div>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={exportToImage}
                            className="!min-w-0 !px-2"
                            title={t('download_image')}
                        >
                            <ImageDown size={16} />
                        </Button>
                    </div>
                </div>

                {/* Legend & Show Inactive Toggle */}
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex gap-4 text-xs">
                        <div className="flex items-center gap-1">
                            <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: COLORS.excellent }}
                            />
                            <span className="text-gray-600">
                                {'>'}95% ({t('excellent')})
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: COLORS.good }}
                            />
                            <span className="text-gray-600">
                                90-95% ({t('good')})
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: COLORS.average }}
                            />
                            <span className="text-gray-600">
                                80-90% ({t('average')})
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: COLORS.poor }}
                            />
                            <span className="text-gray-600">
                                {'<'}80% ({t('needs_improvement')})
                            </span>
                        </div>
                    </div>

                    {inactiveCount > 0 && (
                        <div className="flex items-center gap-2">
                            <Checkbox
                                checked={showInactive}
                                onChange={(e) =>
                                    setShowInactive(e.target.checked)
                                }
                            />
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                                <UserX size={14} />
                                <span>
                                    {t('show_inactive_employees')} (
                                    {inactiveCount})
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {chartData.length === 0 ? (
                    <div className="flex justify-center items-center h-[350px] text-gray-500">
                        {t('no_data_available')}
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={chartData} margin={{ bottom: 60 }}>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e5e7eb"
                            />
                            <XAxis
                                dataKey="displayName"
                                stroke="#9ca3af"
                                fontSize={10}
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                interval={0}
                            />
                            <YAxis stroke="#9ca3af" fontSize={11} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="total_services" radius={[4, 4, 0, 0]}>
                                {chartData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                        fillOpacity={
                                            entry.employee_status === 0
                                                ? 0.5
                                                : 1
                                        }
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}

                {/* Analysis Section */}
                {analysis && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-2 mb-3">
                            <Lightbulb size={16} className="text-amber-500" />
                            <span className="text-sm font-medium text-gray-700">
                                {t('reports.analysis.key_insights')}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="bg-green-50 rounded-lg p-3">
                                <div className="flex items-center gap-1 mb-1">
                                    <Trophy
                                        size={14}
                                        className="text-green-600"
                                    />
                                    <p className="text-xs text-gray-500">
                                        {t('reports.analysis.top_performer')}
                                    </p>
                                </div>
                                <p
                                    className="text-sm font-semibold text-green-600 truncate"
                                    title={
                                        analysis.topPerformer.employee_name ||
                                        String(
                                            analysis.topPerformer.employee_code
                                        )
                                    }
                                >
                                    {analysis.topPerformer.employee_name ||
                                        analysis.topPerformer.employee_code}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {analysis.topPerformer.retention_rate.toFixed(
                                        1
                                    )}
                                    % {t('retention')}
                                </p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-3">
                                <p className="text-xs text-gray-500 mb-1">
                                    {t('reports.analysis.most_services')}
                                </p>
                                <p
                                    className="text-sm font-semibold text-blue-600 truncate"
                                    title={
                                        analysis.mostServices.employee_name ||
                                        String(
                                            analysis.mostServices.employee_code
                                        )
                                    }
                                >
                                    {analysis.mostServices.employee_name ||
                                        analysis.mostServices.employee_code}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {analysis.mostServices.total_services.toLocaleString()}{' '}
                                    {t('services')}
                                </p>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-3">
                                <div className="flex items-center gap-1 mb-1">
                                    <AlertCircle
                                        size={14}
                                        className="text-amber-600"
                                    />
                                    <p className="text-xs text-gray-500">
                                        {t('reports.analysis.needs_attention')}
                                    </p>
                                </div>
                                <p
                                    className="text-sm font-semibold text-amber-600 truncate"
                                    title={
                                        analysis.needsAttention.employee_name ||
                                        String(
                                            analysis.needsAttention
                                                .employee_code
                                        )
                                    }
                                >
                                    {analysis.needsAttention.employee_name ||
                                        analysis.needsAttention.employee_code}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {analysis.needsAttention.retention_rate.toFixed(
                                        1
                                    )}
                                    % {t('retention')}
                                </p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-xs text-gray-500 mb-1">
                                    {t(
                                        'reports.analysis.performance_distribution'
                                    )}
                                </p>
                                <div className="flex gap-2 text-xs mt-1">
                                    <span className="text-green-600 font-medium">
                                        {analysis.distribution.excellent}
                                    </span>
                                    <span className="text-blue-600 font-medium">
                                        {analysis.distribution.good}
                                    </span>
                                    <span className="text-amber-600 font-medium">
                                        {analysis.distribution.average}
                                    </span>
                                    <span className="text-red-600 font-medium">
                                        {analysis.distribution.poor}
                                    </span>
                                </div>
                                <div className="flex gap-2 text-[10px] text-gray-400 mt-0.5">
                                    <span>{'>'}95%</span>
                                    <span>90-95%</span>
                                    <span>80-90%</span>
                                    <span>{'<'}80%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Table Section */}
            <div className="bg-white rounded-md p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                    <Typogaphy className="text-lg font-semibold text-gray-800">
                        {t('detail_statistics')}
                    </Typogaphy>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={exportToExcel}
                        className="!min-w-0 !px-2"
                        title={t('download_excel')}
                    >
                        <Download size={16} />
                    </Button>
                </div>

                <div className="overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">
                                    {t('employee')}
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">
                                    {t('services')}
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">
                                    {t('customers')}
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-semibold text-green-600">
                                    {t('new')}
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-semibold text-red-600">
                                    {t('cancelled')}
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-semibold text-purple-600">
                                    {t('lost_revenue')}
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">
                                    {t('retention')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredData
                                .sort(
                                    (a, b) =>
                                        b.total_services - a.total_services
                                )
                                .map((item, idx) => (
                                    <tr
                                        key={item.employee_code}
                                        className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${item.employee_status === 0 ? 'opacity-60' : ''}`}
                                    >
                                        <td className="px-2 py-2 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                                    style={{
                                                        backgroundColor:
                                                            getRetentionColor(
                                                                item.retention_rate
                                                            ),
                                                    }}
                                                />
                                                <div className="font-medium text-gray-900 flex items-center gap-1">
                                                    {item.employee_name ||
                                                        item.employee_code}
                                                    {item.employee_status ===
                                                        0 && (
                                                        <span className="text-[10px] px-1 py-0.5 bg-red-100 text-red-600 rounded">
                                                            {t('inactive')}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-2 py-2 text-right text-gray-700 font-medium">
                                            {item.total_services.toLocaleString()}
                                        </td>
                                        <td className="px-2 py-2 text-right text-gray-600">
                                            {item.total_customers.toLocaleString()}
                                        </td>
                                        <td
                                            className={`px-2 py-2 text-right ${item.new_services ? 'text-green-600' : 'text-gray-400'}`}
                                        >
                                            {item.new_services
                                                ? `+${item.new_services}`
                                                : '-'}
                                        </td>
                                        <td
                                            className={`px-2 py-2 text-right ${item.cancelled_services ? 'text-red-600' : 'text-gray-400'}`}
                                        >
                                            {item.cancelled_services
                                                ? `-${item.cancelled_services}`
                                                : '-'}
                                        </td>
                                        <td className="px-2 py-2 text-right text-purple-600">
                                            {formatCurrency(item.lost_revenue)}
                                        </td>
                                        <td className="px-2 py-2 text-right">
                                            <span
                                                className="font-medium"
                                                style={{
                                                    color: getRetentionColor(
                                                        item.retention_rate
                                                    ),
                                                }}
                                            >
                                                {item.retention_rate.toFixed(1)}
                                                %
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default EmployeeReport
